/**
 * صفحة هبوط تسجيل المصورين — مخصصة للإعلانات المدفوعة
 * مسار: /join
 * - لا header، لا footer، لا تنقل لبقية الموقع
 * - نموذج التسجيل في صفحة واحدة (بيانات + تخصصات + OTP)
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import AppIcon from '../../components/AppIcon';
import MashhadLogo from '../../components/MashhadLogo';
import Toast from '../../components/ui/Toast';
import OtpInput from '../../components/OtpInput';

// ─── بيانات النموذج ───────────────────────────────────────────────────────────
const SPECIALIZATIONS = [
  { value: 'event',       label: 'تصوير المناسبات' },
  { value: 'product',     label: 'تصوير المنتجات' },
  { value: 'portrait',    label: 'التصوير الشخصي' },
  { value: 'food',        label: 'تصوير الطعام' },
  { value: 'real-estate', label: 'تصوير العقارات' },
  { value: 'wedding',     label: 'حفلات الزفاف' },
];

const CITIES = [
  'الرياض','جدة','الدمام','الخبر','المدينة المنورة','مكة المكرمة',
  'الطائف','أبها','خميس مشيط','جازان','نجران','الباحة','تبوك','حائل','بريدة','سكاكا',
];

const BENEFITS = [
  { icon: 'Calendar',    text: 'حجوزات مباشرة من العملاء' },
  { icon: 'Wallet',      text: 'دفع آمن وسريع' },
  { icon: 'Clock',       text: 'تحكم كامل بجدول مواعيدك' },
  { icon: 'Star',        text: 'بناء سمعة وتقييمات موثوقة' },
];

// ─── الصفحة الرئيسية ──────────────────────────────────────────────────────────
const PhotographerJoin = () => {
  const navigate = useNavigate();
  const formRef       = useRef(null);
  const otpSectionRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '',
    entityType: '', businessName: '', city: '',
    specializations: [],
    agreeToTerms: false,
  });
  const [otpCode,       setOtpCode]       = useState('');
  const [error,         setError]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const [countdown,     setCountdown]     = useState(0);
  const [msegatId,      setMsegatId]      = useState(null);
  const [otpSent,       setOtpSent]       = useState(false);
  const [toastNote,     setToastNote]     = useState(null);
  const [processingMsg, setProcessingMsg] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent,   setTermsContent]   = useState('');
  const [termsLoading,   setTermsLoading]   = useState(false);

  const verifyInFlightRef = useRef(false);

  // ── countdown ──
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const openTermsModal = async () => {
    setShowTermsModal(true);
    if (termsContent) return;
    setTermsLoading(true);
    try {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'page_terms_content').single();
      setTermsContent(data?.value || '');
    } catch { setTermsContent(''); }
    finally { setTermsLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: value.replace(/\D/g,'').slice(0,15) }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMultiSelect = (value) => {
    setFormData(prev => {
      const cur = prev.specializations || [];
      return { ...prev, specializations: cur.includes(value) ? cur.filter(v=>v!==value) : [...cur, value] };
    });
  };

  // ── validation ──
  const validateStep = (step) => {
    setError('');
    if (step === 1) {
      if (!formData.fullName.trim())  { setError('يرجى إدخال الاسم'); return false; }
      if (!formData.email.trim())     { setError('يرجى إدخال البريد الإلكتروني'); return false; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) { setError('البريد الإلكتروني غير صحيح'); return false; }
      if (!formData.phone.trim())     { setError('يرجى إدخال رقم الجوال'); return false; }
      if (formData.phone.length < 9)  { setError('رقم الجوال غير صحيح'); return false; }
      if (!formData.entityType)       { setError('يرجى تحديد نوع الحساب'); return false; }
      if (formData.entityType === 'entity' && !(formData.businessName || '').trim()) {
        setError('يرجى إدخال اسم الاستوديو / الشركة'); return false;
      }
    }
    if (step === 2) {
      if (formData.specializations.length === 0) { setError('يرجى تحديد تخصص واحد على الأقل'); return false; }
    }
    return true;
  };

  const validateBeforeOtp = () => {
    if (!validateStep(1) || !validateStep(2)) return false;
    if (!formData.agreeToTerms) {
      setError('يرجى الموافقة على الشروط والأحكام قبل إرسال كود التحقق');
      return false;
    }
    return true;
  };

  const sendOTP = async () => {
    if (!validateBeforeOtp()) return;
    setLoading(true);
    setError('');
    try {
      const normalized = normalizePhone(formData.phone);

      const { data: existing } = await supabase
        .from('user_profiles').select('role').eq('phone', normalized).maybeSingle();
      if (existing?.role === 'customer')      { setError('هذا الرقم مسجل كحساب عميل. لا يمكن استخدامه كمصور.'); return; }
      if (existing?.role === 'admin')         { setError('هذا الرقم مسجل كحساب مشرف.'); return; }
      if (existing?.role === 'photographer')  { setError('هذا الرقم مسجل بالفعل كمصور. يرجى تسجيل الدخول.'); return; }

      const res = await sendOTPApi(normalized);
      if (res?.id) {
        setMsegatId(res.id);
        setOtpCode('');
        setOtpSent(true);
        setCountdown(60);
        setToastNote({ type: 'success', message: 'تم إرسال كود التحقق إلى جوالك' });
        setTimeout(() => {
          otpSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      } else {
        setError('فشل إرسال كود التحقق. حاول مرة أخرى');
      }
    } catch { setError('حدث خطأ أثناء إرسال الكود'); }
    finally { setLoading(false); }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;
    await sendOTP();
  };

  const verifyAndRegister = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length !== 4) { setError('يرجى إدخال الكود كاملاً'); return; }
    setLoading(true);
    setError('');
    try {
      setProcessingMsg('جاري التحقق من الكود...');
      const normalized = normalizePhone(formData.phone);

      const result = await verifyOtpAndSignIn({
        phone: normalized,
        code,
        role: 'photographer',
        msegatId,
        userData: {
          full_name:        formData.fullName?.trim() || null,
          email:            formData.email?.trim() || null,
          phone:            normalized,
          entity_type:      formData.entityType || 'individual',
          business_name:    formData.businessName || null,
          city:             formData.city || null,
          specializations:  formData.specializations.map(val => {
            const s = SPECIALIZATIONS.find(x => x.value === val);
            return s ? s.label : val;
          }),
          equipment_available: [],
          shooting_methods:    [],
          shooting_method_models: {},
          languages:           [],
        },
      });

      if (result?.error) throw new Error(result.error);

      const finalizeRegistration = async (userId) => {
        // إشعار المشرف بتسجيل مصور جديد
        try {
          await supabase.from('admin_notifications').insert({
            type: 'new_photographer',
            title: '📸 مصور جديد انضم للمنصة',
            message: `المصور "${formData.fullName || ''}" سجّل عبر صفحة الهبوط`,
            related_user_id: userId,
          });
        } catch (_) {}

        // GA4 تتبع التسجيل
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'photographer_sign_up', {
            event_category: 'registration',
            event_label: 'photographer_join_page',
            value: 1,
          });
        }

        // تسجيل اختيار الدور بعد إتمام التسجيل فقط — لمنع تجاوز شاشة الدور عند الإغلاق المبكر
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('native_role_choice_completed', 'true');
        }

        // Snapchat Pixel: حدث إتمام تسجيل مصور
        if (typeof window !== 'undefined' && typeof window.snaptr === 'function') {
          window.snaptr('track', 'SIGN_UP');
        }
        setProcessingMsg('تم التسجيل بنجاح! جاري التوجيه...');
        setToastNote({ type: 'success', message: 'تم التسجيل بنجاح! جاري تحويلك...' });
        setTimeout(() => navigate('/photographer-dashboard'), 1500);
      };

      if (result?.redirectUrl) {
        const url = new URL(result.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { data: authData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash, type: 'email',
        });
        if (verifyError) throw new Error(verifyError.message);
        await finalizeRegistration(authData?.user?.id);
        return;
      }
      if (result?.success && result?.userId) {
        await finalizeRegistration(result.userId);
        return;
      }
      throw new Error('فشل التحقق');
    } catch (e) {
      console.error('Registration error:', e);
      setError(e?.message || 'كود التحقق غير صحيح');
    } finally {
      setLoading(false);
      setProcessingMsg('');
    }
  }, [otpCode, formData, msegatId, navigate]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      verifyAndRegister(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, verifyAndRegister]
  );

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50" dir="rtl" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>

      {/* ══ Hero ══════════════════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/5  rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-5 pt-10 pb-14 flex flex-col items-center text-center gap-6">
          <MashhadLogo variant="light" size="lg" logoType="footer" />

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mt-4">
            انضم كمصور واستقبل
            <br />
            <span style={{ color: '#f88200' }}>حجوزات حقيقية</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg max-w-lg">
            منصة مشهد تربطك بالعملاء مباشرة ، سجّل الآن وحوّل عدستك إلى دخل إضافي
          </p>

          {/* المزايا */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl mt-2">
            {BENEFITS.map((b) => (
              <div key={b.text} className="flex flex-col items-center gap-2 bg-white/10 backdrop-blur-sm rounded-2xl px-3 py-4 border border-white/10">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(248,130,0,0.2)' }}>
                  <AppIcon name={b.icon} size={20} style={{ color: '#f88200' }} />
                </div>
                <p className="text-xs sm:text-sm font-medium text-slate-200 text-center leading-snug">{b.text}</p>
              </div>
            ))}
          </div>

          <button
            onClick={scrollToForm}
            className="mt-2 px-10 py-4 bg-primary hover:opacity-90 text-white font-bold text-lg rounded-2xl shadow-xl shadow-primary/30 transition-all active:scale-95 flex items-center gap-2 border-0"
          >
            <AppIcon name="UserPlus" size={22} />
            انضم الآن
          </button>

          <p className="text-slate-400 text-sm mt-1">
            لديك حساب بالفعل؟{' '}
            <button
              type="button"
              onClick={() => navigate('/photographer-login')}
              className="text-primary font-bold hover:underline border-0 bg-transparent cursor-pointer"
            >
              تسجيل الدخول
            </button>
          </p>
        </div>
      </section>

      {/* ══ النموذج ══════════════════════════════════════════════════════════ */}
      <section ref={formRef} className="max-w-2xl mx-auto px-4 py-10">

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-8">

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              <AppIcon name="AlertCircle" size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-5">
            <h2 className="text-xl font-bold text-slate-900">البيانات الأساسية</h2>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">الاسم  *</label>
              <input name="fullName" value={formData.fullName} onChange={handleChange}
                placeholder="محمد أحمد" dir="rtl"
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">رقم الجوال *</label>
              <input name="phone" value={formData.phone} onChange={handleChange}
                type="tel" inputMode="numeric" placeholder="05xxxxxxxx" dir="rtl"
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition text-right tracking-widest" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">البريد الإلكتروني *</label>
              <input name="email" value={formData.email} onChange={handleChange}
                type="email" placeholder="email@example.com" dir="ltr"
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">نوع الحساب *</label>
              <div className="grid grid-cols-2 gap-3">
                {/* COMING_SOON_STUDIO: لتفعيل استوديو لاحقاً، احذف سطر isComingSoon أو غيّر قيمته لـ false */}
                {[{ val:'individual', label:'مصور فرد', icon:'User' }, { val:'entity', label:'استوديو / شركة', icon:'Building' }].map(opt => {
                  const isComingSoon = opt.val === 'entity';
                  return (
                    <div key={opt.val} className={`relative ${isComingSoon ? 'cursor-not-allowed opacity-60' : ''}`}>
                      <button type="button"
                        onClick={() => !isComingSoon && setFormData(p=>({...p, entityType: opt.val}))}
                        disabled={isComingSoon}
                        className={`w-full p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${
                          formData.entityType === opt.val
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-slate-200 hover:border-primary/40'
                        }`}>
                        <AppIcon name={opt.icon} size={22} className={formData.entityType === opt.val ? 'text-primary' : 'text-slate-400'} />
                        <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                      </button>
                      {isComingSoon && (
                        <span className="absolute -top-2 -right-2 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full">
                          قريباً
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {formData.entityType === 'entity' && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">اسم الاستوديو / الشركة *</label>
                <input name="businessName" value={formData.businessName} onChange={handleChange}
                  placeholder="استوديو مشهد" dir="rtl"
                  className="w-full px-4 py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition" />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">المدينة</label>
              <select name="city" value={formData.city} onChange={handleChange} dir="rtl"
                className="select-rtl w-full py-3 border-2 border-slate-200 focus:border-primary rounded-xl text-sm focus:outline-none transition bg-white">
                <option value="">اختر المدينة </option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="border-t border-slate-100" />

          <div className="space-y-5">
            <h2 className="text-xl font-bold text-slate-900">تخصصاتك</h2>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">تخصصاتك في التصوير *</label>
              <div className="grid grid-cols-2 gap-2">
                {SPECIALIZATIONS.map(s => {
                  const sel = formData.specializations.includes(s.value);
                  return (
                    <button key={s.value} type="button" onClick={() => handleMultiSelect(s.value)}
                      className={`p-3 rounded-xl border-2 text-sm text-right transition-all flex items-center gap-2 ${
                        sel ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-slate-200 hover:border-primary/40 text-slate-700'
                      }`}>
                      {sel && <AppIcon name="Check" size={14} className="shrink-0" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div ref={otpSectionRef} className="border-t border-slate-100 pt-8 space-y-5 scroll-mt-24">
            <h2 className="text-xl font-bold text-slate-900">التحقق من رقم الجوال</h2>
            {otpSent && (
              <p className="text-slate-500 text-sm">
                أدخل الكود المرسل إلى <span className="font-bold text-slate-700" dir="ltr">{formData.phone}</span>
              </p>
            )}

            {!otpSent ? (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-700 leading-relaxed">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span>
                      أوافق على{' '}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTermsModal(); }}
                        className="font-bold text-primary hover:underline inline p-0 border-0 bg-transparent cursor-pointer"
                      >
                        الشروط والأحكام
                      </button>
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={loading || !formData.agreeToTerms}
                  className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0"
                >
                  {loading
                    ? <><AppIcon name="Loader" size={20} className="animate-spin" />جاري الإرسال...</>
                    : <><AppIcon name="Send" size={20} />إرسال كود التحقق</>}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-4 text-center">
                    أدخل كود التحقق (4 أرقام)
                  </label>
                  <OtpInput
                    value={otpCode}
                    onChange={setOtpCode}
                    onComplete={handleOtpComplete}
                    disabled={loading}
                    autoFocus={otpSent}
                    boxClassName="w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none transition"
                  />
                </div>

                <button type="button" onClick={verifyAndRegister}
                  disabled={loading || otpCode.length !== 4}
                  className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2 border-0">
                  {loading
                    ? <><AppIcon name="Loader" size={20} className="animate-spin" />جاري التسجيل...</>
                    : <><AppIcon name="Check" size={20} />إكمال التسجيل</>}
                </button>
                {processingMsg && <p className="text-xs text-slate-500 text-center">{processingMsg}</p>}

                <div className="text-center text-sm">
                  {countdown > 0
                    ? <p className="text-slate-500">إعادة الإرسال بعد <span className="font-bold text-primary">{countdown}</span> ثانية</p>
                    : <button type="button" onClick={resendOTP} className="text-primary font-bold hover:underline border-0 bg-transparent cursor-pointer">إعادة إرسال الكود</button>}
                </div>
              </>
            )}
          </div>


        </div>
      </section>

      {/* Toast */}
      {toastNote && <Toast notification={toastNote} onClose={() => setToastNote(null)} duration={5000} />}

      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <AppIcon name="FileText" size={18} className="text-primary" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">الشروط والأحكام</h2>
              </div>
              <button type="button" onClick={() => setShowTermsModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition">
                <AppIcon name="X" size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {termsLoading
                ? <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>
                : termsContent
                  ? <div className="text-right" dangerouslySetInnerHTML={{ __html: termsContent }} />
                  : <div className="text-center py-12 text-slate-400 text-sm">لم يتم إضافة محتوى الشروط والأحكام بعد</div>}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-slate-100 bg-slate-50">
              <button type="button" onClick={() => setShowTermsModal(false)} className="px-6 py-2.5 bg-primary text-white text-sm font-bold rounded-xl hover:opacity-90 transition">
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PhotographerJoin;
